import { useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '@/pages/admin-dashboard/components/NotificationBell';
import {
  GRUPOS_ADMIN,
  HOME_ADMIN,
  HOME_PUBLICA,
  rotaAdminAtual,
  rotasDoGrupo,
  trilhaDeMigalhas,
  type GrupoAdmin,
} from '@/lib/navegacao';

// ── Quem pode acessar o painel administrativo ─────────────────────────────
// Para liberar instrutores também, troque para: ['admin', 'instructor']
const ALLOWED_ROLES = ['admin'];

/**
 * Casca do painel administrativo.
 *
 * Ela é a ÚNICA dona da navegação daqui para dentro: menu lateral, breadcrumb,
 * identidade e a saída para o site. As páginas em /admin/* renderizam somente
 * conteúdo — nenhuma desenha o próprio cabeçalho, porque era exatamente daí que
 * saíam os "voltar" para /dashboard e para o site público.
 *
 * Menu e breadcrumb leem o mesmo registro (src/lib/navegacao.ts): não há como o
 * item destacado no menu discordar da migalha exibida.
 */
export default function AdminLayout() {
  const { user, userRole, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState<string[]>(['silex', 'cursos', 'vendas', 'operacao']);

  // 1) Sessão ainda carregando → spinner (não decide nada ainda)
  if (loading) {
    return (
      <div className="min-h-screen bg-[#07070D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // 2) Não logado → manda para a home
  if (!user) return <Navigate to={HOME_PUBLICA} replace />;

  // 3) Logado mas sem permissão → tela de acesso restrito (sem loop de redirect)
  // ┌──────────────────────────────────────────────────────────────────────┐
  // │ GUARDA DE PAPEL (removível). Para desligar a checagem de papel, apague │
  // │ este bloco "if" inteiro. Para liberar instrutores, edite ALLOWED_ROLES │
  // │ lá no topo do arquivo.                                                 │
  // └──────────────────────────────────────────────────────────────────────┘
  if (!userRole || !ALLOWED_ROLES.includes(userRole)) {
    return (
      <div className="min-h-screen bg-[#07070D] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 flex items-center justify-center bg-red-500/10 border border-red-500/30 rounded-2xl mx-auto mb-4">
            <i className="ri-lock-2-line text-2xl text-red-400"></i>
          </div>
          <h1 className="text-lg font-bold text-white mb-1">Acesso restrito</h1>
          <p className="text-sm text-gray-400 mb-6">Esta área é exclusiva para administradores.</p>
          <button
            onClick={() => navigate(HOME_PUBLICA)}
            className="px-5 py-2.5 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A0A0F] rounded-xl font-bold text-sm transition-all cursor-pointer"
          >
            Voltar ao site
          </button>
        </div>
      </div>
    );
  }
  // ── fim da guarda de papel ────────────────────────────────────────────────

  const rotaAtual = rotaAdminAtual(location.pathname);
  const migalhas = trilhaDeMigalhas(location.pathname);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const grupoAtivo = (grupo: GrupoAdmin) => {
    if (grupo.path) return location.pathname === grupo.path;
    return rotaAtual?.grupo === grupo.id;
  };

  const handleGrupoClick = (grupo: GrupoAdmin) => {
    if (grupo.path) { navigate(grupo.path); return; }
    toggleExpand(grupo.id);
  };

  const nomeExibido = profile?.full_name || profile?.email || user.email || 'Administrador';

  return (
    <div className="flex min-h-screen bg-[#080812]">
      {/* ───────────────────────── Sidebar ───────────────────────── */}
      <aside
        className={`sticky top-0 h-screen flex flex-col bg-[#080812] border-r border-white/[0.08] transition-all duration-300 shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
      >
        {/* Header / logo — clicável → Painel de Controle */}
        <div className={`flex items-center h-16 border-b border-white/[0.08] px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <button
            onClick={() => navigate(HOME_ADMIN)}
            title="Ir para o Painel"
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-lg shrink-0">
              <i className="ri-shield-star-line text-[#0A0A0F] text-sm"></i>
            </div>
            {!collapsed && (
              <div className="text-left">
                <p className="text-xs font-black text-[#F5F5F0] leading-none group-hover:text-[#D4AF37] transition-colors">Admin Panel</p>
                <p className="text-[9px] text-[#D4AF37] tracking-widest uppercase leading-none mt-0.5">SB Family</p>
              </div>
            )}
          </button>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              title="Recolher menu"
              className="w-6 h-6 flex items-center justify-center rounded-md text-gray-500 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-menu-fold-line text-sm"></i>
            </button>
          )}
        </div>

        {/* Botão de expandir quando recolhido */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            title="Expandir menu"
            className="mx-auto mt-3 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
          >
            <i className="ri-menu-unfold-line text-sm"></i>
          </button>
        )}

        {/* Navegação */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {GRUPOS_ADMIN.map((grupo) => {
            const filhos = grupo.path ? [] : rotasDoGrupo(grupo.id);
            const ativo = grupoAtivo(grupo);
            const aberto = expanded.includes(grupo.id);
            return (
              <div key={grupo.id}>
                <button
                  onClick={() => handleGrupoClick(grupo)}
                  title={collapsed ? grupo.label : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all cursor-pointer group ${
                    ativo
                      ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/20'
                      : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${ativo ? 'text-[#D4AF37]' : ''}`}>
                    <i className={`${grupo.icone} text-base`}></i>
                  </span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-xs font-semibold whitespace-nowrap">{grupo.label}</span>
                      {filhos.length > 0 && (
                        <span className={`w-3 h-3 flex items-center justify-center transition-transform ${aberto ? 'rotate-90' : ''}`}>
                          <i className="ri-arrow-right-s-line text-xs"></i>
                        </span>
                      )}
                    </>
                  )}
                </button>

                {/* Sub-itens */}
                {!collapsed && filhos.length > 0 && aberto && (
                  <div className="ml-7 mt-0.5 space-y-0.5 border-l border-white/[0.08] pl-3">
                    {filhos.map((filho) => {
                      const filhoAtivo = location.pathname === filho.path;
                      return (
                        <button
                          key={filho.path}
                          onClick={() => navigate(filho.path)}
                          aria-current={filhoAtivo ? 'page' : undefined}
                          className={`w-full text-left px-2 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                            filhoAtivo ? 'text-[#D4AF37] font-semibold' : 'text-gray-500 hover:text-gray-200'
                          }`}
                        >
                          {filho.titulo}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Rodapé — Ver site (nova aba) + Sair */}
        <div className="p-3 border-t border-white/[0.08] space-y-1">
          {/*
            NOVA ABA de propósito: o admin inspeciona o site sem perder o painel.
            É a ÚNICA saída daqui para o site público — e ela nunca substitui a
            aba atual, então nenhum "voltar" do painel leva para fora dele.
          */}
          <a
            href={HOME_PUBLICA}
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? 'Ver site (nova aba)' : undefined}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-gray-500 hover:text-white hover:bg-white/5 transition-all cursor-pointer ${collapsed ? 'justify-center' : ''}`}
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0"><i className="ri-external-link-line text-sm"></i></span>
            {!collapsed && (
              <>
                <span className="text-xs font-semibold whitespace-nowrap">Ver site</span>
                <span className="ml-auto text-[9px] text-gray-600 whitespace-nowrap">nova aba</span>
              </>
            )}
          </a>
          <button
            onClick={() => signOut()}
            title={collapsed ? 'Sair' : undefined}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-red-400/80 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer ${collapsed ? 'justify-center' : ''}`}
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0"><i className="ri-logout-box-r-line text-sm"></i></span>
            {!collapsed && <span className="text-xs font-semibold whitespace-nowrap">Sair</span>}
          </button>
        </div>
      </aside>

      {/* ─────────────────── Conteúdo da sub-página ─────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar única — breadcrumb sempre visível */}
        <header className="sticky top-0 z-40 h-16 bg-[#0A0A14]/95 backdrop-blur-xl border-b border-white/[0.08] flex items-center justify-between px-6 shrink-0">
          <div className="min-w-0">
            <nav aria-label="Trilha de navegação" className="flex items-center gap-1.5 flex-wrap">
              {migalhas.map((migalha, i) => {
                const ultima = i === migalhas.length - 1;
                return (
                  <span key={`${migalha.label}-${i}`} className="flex items-center gap-1.5">
                    {i > 0 && <i className="ri-arrow-right-s-line text-[10px] text-gray-600"></i>}
                    {migalha.path && !ultima ? (
                      <Link
                        to={migalha.path}
                        className="text-[11px] text-gray-500 hover:text-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {migalha.label}
                      </Link>
                    ) : (
                      <span
                        aria-current={ultima ? 'page' : undefined}
                        className={`text-[11px] whitespace-nowrap ${ultima ? 'text-[#D4AF37] font-semibold' : 'text-gray-500'}`}
                      >
                        {migalha.label}
                      </span>
                    )}
                  </span>
                );
              })}
            </nav>
            <h1 className="text-sm font-bold text-[#F5F5F0] flex items-center gap-2 mt-0.5 truncate">
              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${rotaAtual?.icone ?? 'ri-dashboard-3-line'} text-[#D4AF37] text-sm`}></i>
              </span>
              <span className="truncate">{rotaAtual?.titulo ?? 'Painel'}</span>
              {rotaAtual?.descricao && (
                <span className="hidden xl:inline text-[10px] font-normal text-gray-500 truncate">
                  · {rotaAtual.descricao}
                </span>
              )}
            </h1>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <NotificationBell />
            {/* Identidade real do perfil logado — sem nome hardcoded. */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl">
              <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-lg shrink-0">
                <i className="ri-shield-star-fill text-[#0A0A0F] text-xs"></i>
              </div>
              <div className="hidden sm:block max-w-[10rem]">
                <p className="text-[10px] font-black text-[#D4AF37] leading-none truncate">{nomeExibido}</p>
                <p className="text-[9px] text-[#D4AF37]/60 leading-none mt-0.5">Administrador</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
